import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PaymentFields, paymentMissing, useRkfBanks } from "@/components/DisburseDialog";
import { RequestDetailsLoader } from "@/components/RequestDetailsPanel";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const blank = () => ({ pay_date: new Date().toISOString().slice(0, 10), mode: "NEFT", utr: "", paid_from_id: "", proof: [], note: "" });
export const expBalance = (e) => Math.round((Number(e?.net_payable || 0) - Number(e?.paid_amount || 0)) * 100) / 100;

export function ExpensePayDialog({ exp, selfPay, onClose, onSaved }) {
  const banks = useRkfBanks();
  const [f, setF] = useState(blank());
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (exp) { setF(blank()); setAmount(String(expBalance(exp))); } }, [exp]);
  const bal = expBalance(exp);
  const amt = Number(amount || 0);
  const block = amt <= 0 ? "Enter the amount" : amt > bal + 0.01 ? `Amount cannot exceed the balance ${inr(bal)}` : paymentMissing(f, selfPay);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/v2/expenses/${exp.id}/pay`, { pay_date: f.pay_date, mode: f.mode, utr: f.utr, paid_from_id: f.paid_from_id, amount: amt, proof_file_id: f.proof[0]?.id, note: f.note });
      toast.success(selfPay ? "Paid – marked Self-paid (Accounts), Super Admin notified" : "Payment posted");
      onClose(); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!exp} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto overscroll-contain" data-testid="expense-pay-dialog">
        <DialogHeader><DialogTitle>Post payment · {exp?.invoice_no}</DialogTitle></DialogHeader>
        <div className="text-sm" data-testid="expense-pay-summary">{exp?.vendor_name} · Net payable <b className="font-mono">{inr(exp?.net_payable)}</b>
          {" "}· TDS {exp?.tds_section_code || "NONE"} {exp?.tds_rate || 0}% = {inr(exp?.tds_amount)} (as verified)
          {Number(exp?.paid_amount || 0) > 0 && <> · Already paid {inr(exp?.paid_amount)}</>}</div>
        {selfPay && <p className="text-xs rounded border border-amber-300 bg-amber-50 p-2 text-amber-900" data-testid="expense-pay-self-note">This bill is payable to you: only after Final approval by the Super Admin, to your registered bank account, with UTR and proof. It is marked "Self-paid (Accounts)" and the Super Admin is notified.</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label>Amount * <span className="text-slate-400 font-normal">(balance {inr(bal)})</span></Label>
            <Input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} data-testid="expense-pay-amount" /></div>
          <div className="text-xs self-end pb-2" data-testid="expense-pay-balance">{amt > 0 && amt < bal - 0.01 ? <span className="text-amber-800">Part payment – {inr(bal - amt)} stays payable</span> : <span className="text-slate-500">Full balance</span>}</div>
        </div>
        <PaymentFields f={f} setF={setF} banks={banks} selfPay={selfPay} testId="expense-pay" />
        <div><Label>Note</Label><Textarea rows={2} value={f.note} onChange={e => setF({ ...f, note: e.target.value })} data-testid="expense-pay-note" /></div>
        {exp && <details className="rounded border p-2" open data-testid="expense-pay-details"><summary className="text-sm cursor-pointer">Payee bank (masked) & approval history</summary><div className="mt-2"><RequestDetailsLoader kind="expense" id={exp.id} /></div></details>}
        {block && <p className="text-xs text-rose-700" data-testid="expense-pay-error">{block}</p>}
        <DialogFooter><Button onClick={submit} disabled={busy || !!block} data-testid="expense-pay-save">{busy ? "Saving…" : `Pay ${inr(amt)}`}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ExpensePayCorrectDialog({ exp, onClose, onSaved }) {
  const banks = useRkfBanks();
  const pay = (exp?.payments || []).slice(-1)[0];
  const [f, setF] = useState(blank());
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (pay) setF({ pay_date: (pay.pay_date || "").slice(0, 10), mode: pay.mode || "NEFT", utr: pay.utr || "", paid_from_id: pay.paid_from_id || "", proof: pay.proof_file_id ? [{ id: pay.proof_file_id, original_filename: "Current proof" }] : [], note: pay.note || "" });
    setReason("");
  }, [exp]); // eslint-disable-line react-hooks/exhaustive-deps
  const block = paymentMissing(f, pay?.self_paid) || (reason.trim().length < 5 ? "A reason is required" : null);
  const submit = async () => {
    setBusy(true);
    try {
      await api.put(`/v2/expenses/${exp.id}/payments/${pay.id}`, { pay_date: f.pay_date, mode: f.mode, utr: f.utr, paid_from_id: f.paid_from_id, proof_file_id: f.proof[0]?.id, note: f.note, reason });
      toast.success("Payment corrected"); onClose(); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!exp} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto overscroll-contain" data-testid="expense-pay-correct-dialog">
        <DialogHeader><DialogTitle>Correct payment · {exp?.invoice_no}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Last payment {inr(pay?.amount)} on {(pay?.pay_date || "").slice(0, 10)}. Accounts can correct within 7 days; after that only the Super Admin. Every change is logged.</p>
        <PaymentFields f={f} setF={setF} banks={banks} selfPay={pay?.self_paid} testId="expense-pay-correct" />
        <div><Label>Note</Label><Textarea rows={2} value={f.note} onChange={e => setF({ ...f, note: e.target.value })} /></div>
        <div><Label>Reason for correction *</Label><Textarea rows={2} value={reason} onChange={e => setReason(e.target.value)} data-testid="expense-pay-correct-reason" /></div>
        {(exp?.payment_changes || []).length > 0 && (
          <div className="rounded border p-2 text-xs" data-testid="expense-pay-change-log">
            <div className="font-semibold mb-1">Change log</div>
            {exp.payment_changes.map((c, i) => <div key={i}>{(c.at || "").slice(0, 10)} · {c.by_name}: {c.changes.map(x => `${x.field} ${x.old || "—"} → ${x.new || "—"}`).join("; ")} — {c.reason}</div>)}
          </div>
        )}
        {block && <p className="text-xs text-rose-700">{block}</p>}
        <DialogFooter><Button disabled={busy || !!block} onClick={submit} data-testid="expense-pay-correct-save">{busy ? "Saving…" : "Save correction"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
